/**
 * rs-feeding-mini – behaviour for the "Plan my order" calculator.
 *
 * Drives every <rs-feeding-mini> on the page, whatever chrome wraps it:
 *   - sections/rs-feeding-mini.liquid  sticky panel (raw-food collection + PDPs)
 *   - blocks/_rs-build-your-box.liquid inline <details> disclosure (BYOB)
 * The shared markup is snippets/rs-feeding-calculator.liquid.
 *
 *  - Live computes daily feed + pack count as inputs change (no Submit button).
 *  - Persists the customer's inputs in localStorage so the plan survives nav.
 *  - Reads /cart.js and counts line-items whose product_id is in the target
 *    collection (IDs are rendered into data-product-ids at Liquid time).
 *  - Listens for the theme's `cart:update` event to re-sync after adds.
 *  - Accepts ?plan=packs:weeks:pet:activity from the full calculator to
 *    pre-fill inputs, auto-expand the panel, and show a dismissible banner.
 *
 * Idempotent per element; safe if the section is re-rendered by section
 * editor or shopify:section:load.
 */

(() => {
  const STORAGE_KEY = 'rsFeedingMini.v1';
  const BANNER_DISMISS_KEY = 'rsFeedingMini.banner.v1';
  const TAG = 'rs-feeding-mini';

  /**
   * Daily-feed % of body weight, keyed by pet + activity.
   * Must stay in sync with the full calculator on /pages/why-raw-diet.
   */
  const PCT = {
    adult: { low: 2, average: 3, active: 4, highly: 5 },
    puppy: { low: 7, average: 8, active: 9, highly: 10 },
  };

  /** Format grams into "g" or "kg" compactly. */
  function formatGrams(g) {
    if (g >= 1000) return (g / 1000).toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1') + ' kg';
    return Math.round(g) + ' g';
  }

  /** Get checked radio value by name inside a form. */
  function getChecked(form, name) {
    const el = form.querySelector(`input[name="${name}"]:checked`);
    return el ? el.value : null;
  }

  /** Persist form inputs to localStorage. */
  function saveState(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_err) {
      /* private-mode / quota – safely ignore */
    }
  }

  function loadState() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_err) {
      return null;
    }
  }

  /** Parse the ?plan= query param. Returns null if absent or malformed. */
  function parsePlanParam() {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('plan');
    if (!raw) return null;

    const parts = raw.split(':');
    if (parts.length < 4) return null;

    const packs = parseInt(parts[0], 10);
    const weeks = parseInt(parts[1], 10);
    const pet = parts[2];
    const activity = parts[3];

    if (!Number.isFinite(packs) || packs <= 0) return null;
    if (!Number.isFinite(weeks) || weeks <= 0) return null;
    if (!(pet in PCT)) return null;
    if (!(activity in PCT.adult)) return null;

    return { packs, weeks, pet, activity };
  }

  /** Remove the plan query param so a refresh doesn't re-trigger the banner. */
  function clearPlanParamFromUrl() {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('plan');
      window.history.replaceState({}, '', url.toString());
    } catch (_err) {
      /* non-fatal */
    }
  }

  /** Set a radio group to a value. */
  function setRadio(form, name, value) {
    const el = form.querySelector(`input[name="${name}"][value="${value}"]`);
    if (el) el.checked = true;
  }

  /** Fetch the current cart. Returns null on failure (network, non-200). */
  async function fetchCart() {
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (_err) {
      return null;
    }
  }

  /** Count items in the cart whose product_id matches our target collection. */
  function countInCart(cart, productIds) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    let total = 0;
    for (const item of cart.items) {
      if (productIds.has(String(item.product_id))) {
        total += item.quantity;
      }
    }
    return total;
  }

  /** Initialise a single <rs-feeding-mini> element. */
  function init(root) {
    if (root.dataset.rsFmReady === '1') return;
    root.dataset.rsFmReady = '1';

    const panel = root.querySelector('[data-rs-fm-panel]');
    const body = root.querySelector('[data-rs-fm-body]');
    const summary = root.querySelector('[data-rs-fm-toggle]');
    const closeBtn = root.querySelector('[data-rs-fm-close]');
    const form = root.querySelector('[data-rs-fm-form]');
    const banner = root.querySelector('[data-rs-fm-banner]');
    const bannerClose = root.querySelector('[data-rs-fm-banner-close]');
    const fullLink = root.querySelector('[data-rs-fm-full-link]');

    const resultEl = root.querySelector('[data-rs-fm-result]');
    const emptyEl = root.querySelector('[data-rs-fm-empty]');
    const elDaily = root.querySelector('[data-rs-fm-daily]');
    const elTotal = root.querySelector('[data-rs-fm-total]');
    const elNeeded = root.querySelector('[data-rs-fm-needed]');
    const elNeededPlural = root.querySelector('[data-rs-fm-needed-plural]');
    const elInCart = root.querySelector('[data-rs-fm-in-cart]');
    const elProgress = root.querySelector('[data-rs-fm-progress]');
    const cartStatus = root.querySelector('[data-rs-fm-cart-status]');
    const elSummaryInCart = root.querySelector('[data-rs-fm-summary-in-cart]');
    const elSummaryNeeded = root.querySelector('[data-rs-fm-summary-needed]');
    const elSummaryPlural = root.querySelector('[data-rs-fm-summary-plural]');

    if (!panel || !form) return;

    const packGrams = parseInt(root.dataset.packGrams || '1000', 10) || 1000;
    const productIds = new Set(
      (root.dataset.productIds || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    );

    // Hide the "Full calculator" link if no URL was configured.
    if (fullLink && !fullLink.getAttribute('href')) {
      fullLink.hidden = true;
    }

    // ---------- state ----------
    /** @type {number} last computed packs-needed (0 when no weight entered) */
    let needed = 0;
    /** @type {number} last-known in-cart pack count */
    let inCart = 0;

    // ---------- expand / collapse ----------
    // Two shapes of panel. The sticky section uses a custom button + hidden
    // body it drives itself. The Build Your Box disclosure uses a native
    // <details>, which already gives us keyboard operation, the correct
    // expanded state and focus handling, so we must not fight it: no click
    // handler, and setExpanded just toggles .open.
    const isDetails = panel.tagName === 'DETAILS';

    function setExpanded(isOpen) {
      if (isDetails) {
        panel.open = isOpen;
        return;
      }
      panel.setAttribute('data-rs-fm-expanded', String(isOpen));
      summary.setAttribute('aria-expanded', String(isOpen));
      if (body) body.hidden = !isOpen;
    }

    if (!isDetails) {
      summary.addEventListener('click', () => {
        const open = panel.getAttribute('data-rs-fm-expanded') === 'true';
        setExpanded(!open);
      });
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', () => setExpanded(false));
    }

    // ---------- compute ----------
    function updateSummary() {
      if (elSummaryInCart) elSummaryInCart.textContent = String(inCart);
      if (elSummaryNeeded) elSummaryNeeded.textContent = needed > 0 ? String(needed) : '–';
      if (elSummaryPlural) elSummaryPlural.textContent = needed === 1 ? '' : 's';
    }

    function updateCartStatus() {
      if (!cartStatus || !elInCart) return;
      elInCart.textContent = String(inCart);

      if (!elProgress) return;

      if (needed <= 0) {
        elProgress.textContent = '';
        cartStatus.classList.remove('rs-fm__cart-status--complete');
        return;
      }

      if (inCart >= needed) {
        elProgress.textContent = `You've reached your plan of ${needed}. Nice.`;
        cartStatus.classList.add('rs-fm__cart-status--complete');
      } else {
        const remaining = needed - inCart;
        elProgress.textContent = ` (need ${remaining} more)`;
        cartStatus.classList.remove('rs-fm__cart-status--complete');
      }
    }

    function compute() {
      const pet = getChecked(form, 'pet') || 'adult';
      const activity = getChecked(form, 'activity') || 'average';
      const weeks = parseInt(getChecked(form, 'weeks') || '1', 10);
      const weightEl = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="weight"]'));
      const weight = weightEl ? parseFloat(weightEl.value) : NaN;

      saveState({ pet, activity, weeks, weight: Number.isFinite(weight) ? weight : null });

      if (!Number.isFinite(weight) || weight <= 0) {
        needed = 0;
        if (resultEl) resultEl.hidden = true;
        if (emptyEl) emptyEl.hidden = false;
        updateSummary();
        return;
      }

      const pct = (PCT[pet] || PCT.adult)[activity] || PCT.adult.average;
      const dailyGrams = weight * (pct / 100) * 1000;
      const periodGrams = dailyGrams * 7 * weeks;
      needed = Math.ceil(periodGrams / packGrams);

      if (elDaily) elDaily.textContent = formatGrams(dailyGrams);
      if (elTotal) elTotal.textContent = formatGrams(periodGrams);
      if (elNeeded) elNeeded.textContent = String(needed);
      if (elNeededPlural) elNeededPlural.textContent = needed === 1 ? '' : 's';

      if (resultEl) resultEl.hidden = false;
      if (emptyEl) emptyEl.hidden = true;

      updateSummary();
      updateCartStatus();
    }

    form.addEventListener('input', compute);
    form.addEventListener('change', compute);

    // ---------- cart sync ----------
    /** True when this instance renders a pack counter worth keeping in sync. */
    const hasCounter = Boolean(elInCart || elSummaryInCart);

    async function refreshCart() {
      // The Build Your Box disclosure has no counter (the builder only writes
      // to the cart on submit), so there is nothing to fetch for.
      if (!hasCounter) return;
      const cart = await fetchCart();
      inCart = countInCart(cart, productIds);
      updateSummary();
      updateCartStatus();
    }

    document.addEventListener('cart:update', refreshCart);
    // Some theme components dispatch non-namespaced cart events; catch those too.
    document.addEventListener('cart:refresh', refreshCart);

    // ---------- state restore ----------
    const saved = loadState();
    if (saved && typeof saved === 'object') {
      if (saved.pet) setRadio(form, 'pet', saved.pet);
      if (saved.activity) setRadio(form, 'activity', saved.activity);
      if (saved.weeks) setRadio(form, 'weeks', String(saved.weeks));
      if (saved.weight != null) {
        const weightEl = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="weight"]'));
        if (weightEl) weightEl.value = String(saved.weight);
      }
    }

    // ---------- ?plan= handling ----------
    const plan = parsePlanParam();
    if (plan) {
      setRadio(form, 'pet', plan.pet);
      setRadio(form, 'activity', plan.activity);
      setRadio(form, 'weeks', String(plan.weeks));
      // We don't know the customer's weight from the param (the calculator
      // outputs pack count, not weight), so back-solve: needed = ceil(weight *
      // (pct/100) * 1000 * 7 * weeks / packGrams). Reverse:
      //   weight = plan.packs * packGrams / (7 * weeks * (pct/100) * 1000)
      // This is approximate (round-up means the true weight may be slightly
      // less) but gives the customer a sensible starting point they can tweak.
      const pct = (PCT[plan.pet] || PCT.adult)[plan.activity] || PCT.adult.average;
      const weightApprox = (plan.packs * packGrams) / (7 * plan.weeks * (pct / 100) * 1000);
      const weightRounded = Math.round(weightApprox * 10) / 10;
      const weightEl = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="weight"]'));
      if (weightEl && weightRounded > 0) weightEl.value = String(weightRounded);

      // Populate + show banner unless dismissed this session.
      if (banner) {
        const dismissed = sessionStorage.getItem(BANNER_DISMISS_KEY) === '1';
        if (!dismissed) {
          const elBannerPacks = root.querySelector('[data-rs-fm-banner-packs]');
          const elBannerWeeks = root.querySelector('[data-rs-fm-banner-weeks]');
          const elBannerPlural = root.querySelector('[data-rs-fm-banner-plural]');
          if (elBannerPacks) elBannerPacks.textContent = String(plan.packs);
          if (elBannerWeeks) {
            elBannerWeeks.textContent =
              plan.weeks === 1 ? '1 week' : `${plan.weeks} weeks`;
          }
          if (elBannerPlural) elBannerPlural.textContent = plan.packs === 1 ? '' : 's';
          banner.hidden = false;
        }
      }

      setExpanded(true);
      clearPlanParamFromUrl();
    }

    if (bannerClose && banner) {
      bannerClose.addEventListener('click', () => {
        banner.hidden = true;
        try {
          sessionStorage.setItem(BANNER_DISMISS_KEY, '1');
        } catch (_err) {
          /* ignore */
        }
      });
    }

    // ---------- first paint ----------
    compute();
    refreshCart();
  }

  function initAll() {
    document.querySelectorAll(TAG).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
